import React from 'react';
import { Breadcrumb } from 'semantic-ui-react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import * as _ from 'lodash';
import FlexContainer from 'common/components/atoms/FlexContainer';
import LogoSvg from 'common/images/LogoSvg';
import OutsideAlerter from 'common/OutsideAlerter';
import { history } from 'common/helpers';
import { ENDPOINT } from 'common/constants';
import { sliceString } from 'common/utils';
import colors from 'common/styles/colors';
import SitesDropdown from './SitesDropdown';
import BreadcrumbContainer from './components/atoms/BreadCrumbContainer';
import { getBaseUrl } from '../DomainName/DomainConstants';

// The machine-detail tabs (from MachineDetails.js). The active tab is appended to the
// breadcrumb as the current page, e.g. First Floor > Asset 1 > FMEA.
const MACHINE_TABS = [
  'overview', 'health', 'baseline', 'charts', 'activity',
  'fmea', 'instrumentation-alerts', 'devices', 'attachments', 'info', '3d-view'
];

// Tab slugs whose i18n key is not the slug in camelCase.
const MACHINE_TAB_KEYS = { '3d-view': 'threeDView' };

// Tier 1: global identity (logo + account) on the left, global actions
// (help / admin / avatar, passed in from HomePage) on the right, all in flow.
const GlobalTier = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 34px;
  min-width: 0;
  padding-right: 18px;
  font-family: 'Petasense Open Sans';
`;

// Tier 2: the context lane. Breadcrumb (one line) on the left, the machine
// status cluster (condition + state + criticality + add, passed in from HomePage)
// on the right, all in flow so they align automatically.
const ContextLane = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  min-height: 32px;
  padding: 0 18px 0 20px;
  /* the divider between Tier 1 (global chrome) and Tier 2 (context lane), per the design */
  border-top: 1px solid ${props => props.theme.colors.greyL};
`;

const SideGroup = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1 1 auto;
`;

// Right-hand groups hold the actions / status; they never shrink and center their
// contents. `a` override neutralises StyledLink's width:100% so the icons stay compact.
const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 0 0 auto;
  margin-left: 16px;

  /* Neutralise StyledLink's width:100% + margin:3px 0 (doubled selector beats its own
     class) so the action icons sit on one baseline, evenly spaced and vertically centred. */
  && a {
    width: auto;
    margin: 0;
    display: flex;
    align-items: center;
  }

  /* The profile menu was built for the left side menu, where it opens to the right
     and upward. At the right end of this bar that is off the page, so here it opens
     downward, aligned to the avatar's right edge. */
  #profile-dropdown.ui.dropdown .menu,
  #profile-dropdown.ui.dropdown .menu.visible {
    top: calc(100% + 6px) !important;
    bottom: auto !important;
    left: auto !important;
    right: 0 !important;
  }
`;

const LaneTrail = styled(FlexContainer)`
  align-items: center;
  min-width: 0;
`;

// Shown inline in Tier 1 on pages with no breadcrumb (dashboard, assets, etc.) so the
// bar stays a single compact row instead of an almost-empty second tier.
const InlineTitle = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  margin-left: 14px;
  padding-left: 14px;
  border-left: 1px solid ${colors.greyL};
  font-family: 'Petasense Open Sans';
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.disable ? 'gray' : colors.greyXXD};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LogoContainer = styled.div`
  margin: 0 16px 0 20px;
  display: flex;
  align-items: center;
  svg {
    margin-top: 2px;
  }
  cursor: pointer;
`;

const AccountArea = styled(FlexContainer)`
  align-items: center;
  min-width: 0;
`;

const BreadcrumbSection = styled(Breadcrumb.Section)`
  font-family: 'Petasense Open Sans';
  font-size: 12px;
  font-weight: 600;
  color: ${colors.greyXD};
  text-overflow: clip;
  white-space: nowrap;
`;

// The current ("you are here") segment: heavier weight + an accent underline whose
// colour is set inline (partner-aware) so no custom prop leaks onto the DOM node.
const CurrentBreadcrumbSection = styled(Breadcrumb.Section)`
  font-family: 'Petasense Open Sans';
  font-size: 12px;
  font-weight: 700;
  color: ${colors.greyXXD};
  text-overflow: clip;
  white-space: nowrap;
  border-bottom: 2px solid transparent;
  padding-bottom: 1px;
`;

const BreadcrumbDivider = styled(Breadcrumb.Divider)`
  color: ${colors.greyXD};
`;

// The machine-page ancestor collapse: a single dot that reveals the full path on
// tap. No horizontal scroll, no ellipsis (the current segment always stays readable).
const DotWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const DotButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid ${colors.greyL};
  background: ${colors.greyXL};
  cursor: pointer;
  padding: 0;

  &:hover {
    background: ${colors.greyL};
    border-color: ${colors.greyD};
  }

  span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${colors.greyXD};
  }
`;

const PathPopover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 1000;
  min-width: 180px;
  background: ${colors.white};
  border: 1px solid ${colors.greyL};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(60, 61, 58, 0.16);
  padding: 6px;
`;

const PathRow = styled.div`
  font-family: 'Petasense Open Sans';
  font-size: 12.5px;
  color: ${colors.greyXXD};
  padding: 6px 10px;
  border-radius: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;

  & + & {
    border-top: 1px solid ${colors.greyXL};
  }
`;

export class BreadCrumbHeaderBase extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      title: '',
      localBreadcrumb: [],
      pathOpen: false,
    };
  }

  componentDidMount() {
    this.setTitleAndBreadcrumb();
    this.observeHeight();
  }

  componentDidUpdate(prevProps) {
    this.reportHeight();
    if (
      !_.isEqual(prevProps.location, this.props.location) ||
      !_.isEqual(_.cloneDeep(prevProps.reduxBreadcrumb), _.cloneDeep(this.props.reduxBreadcrumb))
    ) {
      this.setTitleAndBreadcrumb();
      // close the collapsed-path popover when the route/breadcrumb changes
      if (this.state.pathOpen) this.setState({ pathOpen: false });
    }
  }

  componentWillUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  // The bar is one row or two depending on the page, so the layouts below it learn its
  // real height from here (HomePage publishes it, see common/styles/titleBar).
  setContainerRef = (node) => {
    this.container = node;
  };

  observeHeight = () => {
    this.reportHeight();
    if (this.container && typeof window !== 'undefined' && window.ResizeObserver) {
      this.resizeObserver = new window.ResizeObserver(this.reportHeight);
      this.resizeObserver.observe(this.container);
    }
  };

  reportHeight = () => {
    const { onHeightChange } = this.props;
    if (!onHeightChange || !this.container) return;
    const height = Math.round(this.container.getBoundingClientRect().height);
    if (height && height !== this.reportedHeight) {
      this.reportedHeight = height;
      onHeightChange(height);
    }
  };

  humanize = (str) => {
    if (!str) return '';
    return str.split('-').map(n => n.charAt(0).toUpperCase() + n.slice(1)).join(' ');
  }

  getBreadcrumb = () => {
    const { reduxBreadcrumb } = this.props;
    const breadcrumb = [];
    if (reduxBreadcrumb.area.id && reduxBreadcrumb.selected.type !== 'area') {
      breadcrumb.push(reduxBreadcrumb.area.name);
    }
    if (reduxBreadcrumb.machine.id && reduxBreadcrumb.selected.type !== 'machine') {
      breadcrumb.push(reduxBreadcrumb.machine.name);
    }
    if (reduxBreadcrumb.machine_charts.id && reduxBreadcrumb.selected.type !== 'machine_charts') {
      breadcrumb.push(reduxBreadcrumb.machine_charts.name);
    }
    if (reduxBreadcrumb.component.id && reduxBreadcrumb.selected.type !== 'component') {
      breadcrumb.push(reduxBreadcrumb.component.name);
    }
    if (reduxBreadcrumb.location.id && reduxBreadcrumb.selected.type !== 'location') {
      breadcrumb.push(reduxBreadcrumb.location.name);
    }
    return breadcrumb;
  }

  setTitleAndBreadcrumb = () => {
    const { location, reduxBreadcrumb, adminDashboard } = this.props;
    const paths = location.pathname.split('/');
    if (location.pathname === '/') paths[1] = 'dashboard';
    let title = reduxBreadcrumb.selected.type !== 'site' ? reduxBreadcrumb.selected.name : '';
    const breadcrumb = this.getBreadcrumb();
    if (!title || adminDashboard.adminOpen) title = paths[1];
    this.setState({ title: this.humanize(title), localBreadcrumb: this.props.breadCrumb ? this.props.breadCrumb : breadcrumb });
  };

  onLogoClick = () => {
    const { reduxBreadcrumb } = this.props;
    history.push('/');
    if (reduxBreadcrumb.site.id) this.props.handleNodeClick(reduxBreadcrumb.site.id, 'site');
  }

  getLogoUrl = () => (
    `${getBaseUrl()}/${ENDPOINT.ACCOUNT_LOGO}?name=${this.props.logoUrl}`
  );

  getAccentColor = () => {
    const { companyReducer } = this.props;
    if (companyReducer && companyReducer.partner && companyReducer.partner.theme && companyReducer.partner.theme.primaryColor) {
      return companyReducer.partner.theme.primaryColor;
    }
    return colors.green;
  }

  togglePathOpen = () => {
    this.setState(prevState => ({ pathOpen: !prevState.pathOpen }));
  };

  closePathOpen = () => {
    if (this.state.pathOpen) this.setState({ pathOpen: false });
  };

  getTitleTooltip = () => {
    const { title } = this.state;
    const { t } = this.props;
    const normalizedTitle = (title || '').toLowerCase();

    if (normalizedTitle === 'dashboard') return t('topBar.dashboard');
    if (normalizedTitle === 'profile') return t('topBar.profile');

    // Settings / account menu pages
    if (normalizedTitle === 'organisation') return t('home.accountMenu.settings.organisation');
    if (normalizedTitle === 'site profile') return t('home.accountMenu.settings.site');
    if (normalizedTitle === 'users') return t('home.accountMenu.settings.users');
    if (normalizedTitle === 'teams') return t('home.accountMenu.settings.teams');
    if (normalizedTitle === 'saas agreement') return t('home.accountMenu.settings.saasAgreement');
    if (normalizedTitle === 'third party tags') return t('home.accountMenu.settings.thirdPartyTags');
    if (normalizedTitle === 'third party integrations') return t('home.accountMenu.settings.thirdPartyIntegrations');
    if (normalizedTitle === 'tag templates') return t('home.accountMenu.settings.tagTemplates');
    if (normalizedTitle === 'task templates') return t('home.accountMenu.settings.taskTemplates');
    if (normalizedTitle === 'fmea templates') return t('home.accountMenu.settings.fmeaTemplates');
    if (normalizedTitle === 'audit logs') return t('home.accountMenu.settings.auditLogs');
    if (normalizedTitle === 'devices health') return t('home.accountMenu.others.devicesHealth');
    if (normalizedTitle === 'alarm limits') return t('home.accountMenu.others.alarmLimits');

    return title;
  };

  getDisplayTitle = () => {
    const { title } = this.state;
    const { t } = this.props;
    const normalizedTitle = (title || '').toLowerCase();

    if (normalizedTitle === 'dashboard') return t('topBar.dashboard');
    if (normalizedTitle === 'templates') return t('topBar.assetTemplates');
    if (normalizedTitle === 'activity') return t('topBar.activity');
    if (normalizedTitle === 'profile') return t('topBar.profile');
    if (normalizedTitle === 'assets') return t('topBar.assets');
    if (normalizedTitle === 'asset templates') return t('topBar.assetTemplates');
    if (normalizedTitle === 'devices') return t('topBar.devices');
    if (normalizedTitle === 'event definitions') return t('topBar.eventDefinitions');
    if (normalizedTitle === 'diagnostic rules') return t('topBar.diagnosticRules');
    if (normalizedTitle === 'sensor models') return t('topBar.sensorModels');
    if (normalizedTitle === 'automatic alarms') return t('topBar.automaticAlarms');
    if (normalizedTitle === 'reports') return t('topBar.reports');
    if (normalizedTitle === 'instrumentation alerts') return t('topBar.instrumentationAlerts');

    // Settings / account menu pages
    if (normalizedTitle === 'organisation') return t('home.accountMenu.settings.organisation');
    if (normalizedTitle === 'site profile') return t('home.accountMenu.settings.site');
    if (normalizedTitle === 'users') return t('home.accountMenu.settings.users');
    if (normalizedTitle === 'teams') return t('home.accountMenu.settings.teams');
    if (normalizedTitle === 'saas agreement') return t('home.accountMenu.settings.saasAgreement');
    if (normalizedTitle === 'third party tags') return t('home.accountMenu.settings.thirdPartyTags');
    if (normalizedTitle === 'third party integrations') return t('home.accountMenu.settings.thirdPartyIntegrations');
    if (normalizedTitle === 'tag templates') return t('home.accountMenu.settings.tagTemplates');
    if (normalizedTitle === 'task templates') return t('home.accountMenu.settings.taskTemplates');
    if (normalizedTitle === 'fmea templates') return t('home.accountMenu.settings.fmeaTemplates');
    if (normalizedTitle === 'audit logs') return t('home.accountMenu.settings.auditLogs');
    if (normalizedTitle === 'devices health') return t('home.accountMenu.others.devicesHealth');
    if (normalizedTitle === 'alarm limits') return t('home.accountMenu.others.alarmLimits');

    return title;
  };

  renderAccountArea = () => {
    const { accountsArray, user: { user }, checkNewSummaryExists, setAnalystSummaryWarning, discardAnalystSummary, setDiscardAnalystSummary, analystSummaryWarning } = this.props;
    const { title } = this.state;
    return (
      <AccountArea>
        {accountsArray && accountsArray.length > 0 && title !== 'Profile' && (
          <SitesDropdown
            checkNewSummaryExists={checkNewSummaryExists}
            setAnalystSummaryWarning={setAnalystSummaryWarning}
            discardAnalystSummary={discardAnalystSummary}
            analystSummaryWarning={analystSummaryWarning}
            setDiscardAnalystSummary={setDiscardAnalystSummary}
          />
        )}
        {title === 'Profile' && `${user.first_name} ${user.last_name}`}
        {this.props.breadCrumb && this.props.accountsName && (
          <BreadcrumbSection title={this.props.accountsName}>
            {sliceString(this.props.accountsName, 15)}
          </BreadcrumbSection>
        )}
      </AccountArea>
    );
  };

  // The active machine tab (from the URL) as a display label, via the tabs' own i18n
  // keys. `instrumentation-alerts` maps to the camelCase key `instrumentationAlerts`.
  getMachineTabLabel = (slug) => {
    const { t } = this.props;
    const key = MACHINE_TAB_KEYS[slug] || slug.replace(/-([a-z])/g, (m, c) => c.toUpperCase());
    const label = t(`machineDashboard.tabs.${key}`);
    if (!label || label === `machineDashboard.tabs.${key}`) return this.humanize(slug);
    return label;
  };

  // One uniform line: the ancestors (everything above the current page) collapse to a
  // single dot when there is more than one, tap the dot to reveal the full path. The
  // current page (the page title) is the emphasised final segment. No stacked rows.
  renderTrail = (ancestors, current) => {
    const { t, editMode } = this.props;
    const { pathOpen } = this.state;
    const accent = this.getAccentColor();
    // greyed while the hierarchy is in edit mode, as the page title always was
    const currentStyle = editMode ? { borderBottomColor: accent, color: 'gray' } : { borderBottomColor: accent };
    // Collapse only when the trail is genuinely long (3+ ancestors); a machine tab page
    // (area > machine > tab) keeps the machine visible rather than hiding it in the dot.
    const collapse = ancestors.length > 2;
    return (
      <Breadcrumb>
        {collapse ? (
          <DotWrapper>
            <DotButton
              type="button"
              id="breadcrumb-collapse"
              title={t('topBar.showFullPath')}
              aria-label={t('topBar.showFullPath')}
              aria-expanded={pathOpen}
              onClick={this.togglePathOpen}
            >
              <span />
            </DotButton>
            {pathOpen && (
              <OutsideAlerter event="click" open handleClick={this.closePathOpen}>
                <PathPopover>
                  {ancestors.map((path, index) => (
                    <PathRow key={index} title={path}>{path}</PathRow>
                  ))}
                </PathPopover>
              </OutsideAlerter>
            )}
          </DotWrapper>
        ) : (
          ancestors.map((path, index) => (
            <React.Fragment key={index}>
              {index > 0 && <BreadcrumbDivider icon="right angle" />}
              <BreadcrumbSection title={path}>
                {sliceString(path, 22)}
              </BreadcrumbSection>
            </React.Fragment>
          ))
        )}
        {ancestors.length > 0 && <BreadcrumbDivider icon="right angle" />}
        <CurrentBreadcrumbSection title={current} style={currentStyle}>
          {current}
        </CurrentBreadcrumbSection>
      </Breadcrumb>
    );
  };

  render() {
    const {
      editMode,
      handleLogoClick,
      reduxBreadcrumb,
      adminDashboard,
      logoUrl,
      globalActions,
      machineStatus,
      location,
    } = this.props;
    const { localBreadcrumb } = this.state;
    // On a machine-detail tab page (/machines/:id/:tab) show the active tab as the
    // current segment, with the machine folded into the ancestors: First Floor > Asset 1 > FMEA.
    const paths = (location && location.pathname ? location.pathname : '').split('/');
    const machineTab = (paths[1] === 'machines' && paths[2] && MACHINE_TABS.indexOf(paths[3]) !== -1 && reduxBreadcrumb.machine.id)
      ? paths[3] : null;
    let ancestors = localBreadcrumb;
    let currentLabel = this.getDisplayTitle();
    if (machineTab) {
      // getBreadcrumb leaves the selected node out, so the machine is already an ancestor
      // unless the machine itself is selected (e.g. the Charts tab selects its "All Charts" node).
      const machineInTrail = reduxBreadcrumb.selected.type !== 'machine';
      ancestors = reduxBreadcrumb.machine.name && !machineInTrail
        ? localBreadcrumb.concat([reduxBreadcrumb.machine.name])
        : localBreadcrumb;
      currentLabel = this.getMachineTabLabel(machineTab);
    }
    const hasTrail = !adminDashboard.adminOpen && (localBreadcrumb.length > 0 || Boolean(machineTab));
    return (
      <BreadcrumbContainer innerRef={this.setContainerRef}>
        <GlobalTier>
          <SideGroup>
            <LogoContainer onClick={() => handleLogoClick(reduxBreadcrumb.site.id, 'site')}>
              {logoUrl ? (
                <img height="25px" src={this.getLogoUrl()} alt="" />
              ) : (
                <LogoSvg />
              )}
            </LogoContainer>
            {!adminDashboard.adminOpen && this.renderAccountArea()}
            {!hasTrail && (
              <InlineTitle disable={editMode} title={this.getTitleTooltip()}>
                {currentLabel}
              </InlineTitle>
            )}
          </SideGroup>
          {globalActions && <RightGroup>{globalActions}</RightGroup>}
        </GlobalTier>
        {(hasTrail || machineStatus) && (
          <ContextLane>
            <SideGroup>
              {hasTrail && (
                <LaneTrail>
                  {this.renderTrail(ancestors, currentLabel)}
                </LaneTrail>
              )}
            </SideGroup>
            {machineStatus && <RightGroup>{machineStatus}</RightGroup>}
          </ContextLane>
        )}
      </BreadcrumbContainer>
    );
  }
}

const mapStateToProps = (state) => {
  const { machineDetails, sensors, machines, events, breadcrumb, user, adminDashboard, currentAccount, companyReducer } = state;
  return {
    machineDetails,
    sensors,
    machines,
    breadcrumb,
    events,
    reduxBreadcrumb: breadcrumb,
    user,
    adminDashboard,
    logoUrl: currentAccount && currentAccount.logo_url,
    companyReducer
  };
};

export default connect(mapStateToProps)(withTranslation()(BreadCrumbHeaderBase));
